// feature/tipo-fallo-edit — modal for changing a fallo's tipo_fallo
// (the legacy enum on case_type_data). Workflow-affecting:
// confirming the change triggers backend cascade that updates
// sub_tipo + instancia + resultado on cases AND re-applies Sprint
// 58d's requiere_insumo=true cascade for desfavorables. The static
// warning below the dropdown surfaces this so the team confirms
// knowingly.
//
// Backend: PUT /api/cases/:id with {tipo_fallo: <newValue>}.
// Cascade is handled server-side; frontend just refreshes after 200.
import React, { useState } from "react";
import CenteredModal from "./CenteredModal";
import Spinner from "./Spinner";
import { cases as casesApi } from "../api/endpoints";
import { useToast } from "./Toast";

// Display labels mirror the CASE_TAXONOMY.fallo.sub_tipos labels
// from utils/case_taxonomy.js, plus the NO_PEDIR_INSUMO outlier.
const TIPO_FALLO_OPTIONS = [
  ["FAVORABLE_1A",    "Fallo primera instancia — Favorable"],
  ["DESFAVORABLE_1A", "Fallo primera instancia — Desfavorable"],
  ["FAVORABLE_2A",    "Fallo segunda instancia — Favorable"],
  ["DESFAVORABLE_2A", "Fallo segunda instancia — Desfavorable"],
  ["NULIDAD",         "Fallo nulidad"],
  ["NO_PEDIR_INSUMO", "No pedir insumo (no aplica)"],
];

export default function TipoFalloEditModal({ open, c, onClose, onUpdated }) {
  const toast = useToast();
  const [val, setVal] = useState(c?.tipo_fallo || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  // Reset selection when the modal re-opens for a different case.
  React.useEffect(() => {
    if (open) {
      setVal(c?.tipo_fallo || "");
      setErr(null);
    }
  }, [open, c?.id, c?.tipo_fallo]);

  async function confirm() {
    if (!val) {
      setErr("Seleccione un tipo.");
      return;
    }
    if (val === c?.tipo_fallo) {
      onClose?.();
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await casesApi.update(c.id, { tipo_fallo: val });
      toast.push("Tipo de actuación actualizado.", "success");
      onUpdated?.();
      onClose?.();
    } catch (e) {
      const msg = e?.response?.data?.message || e?.response?.data?.error || "Error al guardar.";
      setErr(msg);
    } finally {
      setBusy(false);
    }
  }

  return (
    <CenteredModal
      open={open}
      onClose={busy ? undefined : onClose}
      title="Editar tipo de actuación"
      subtitle={c ? `Caso ${c.case_number}` : undefined}
      size="md"
      footer={
        <>
          <button className="secondary" disabled={busy} onClick={onClose}>Cancelar</button>
          <button disabled={busy || !val} onClick={confirm}>
            {busy && <Spinner color="currentColor" />} Confirmar
          </button>
        </>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <div>
          <label>Tipo de actuación</label>
          <select value={val} onChange={(e) => setVal(e.target.value)} disabled={busy}>
            <option value="">— elegir —</option>
            {TIPO_FALLO_OPTIONS.map(([v, l]) => (
              <option key={v} value={v}>{l}</option>
            ))}
          </select>
        </div>
        <div className="warn-inline" style={{
          padding: 10, fontSize: 12,
          background: "var(--color-warning-bg)",
          borderColor: "var(--color-warning)",
          color: "var(--text-body)",
        }}>
          Cambiar el tipo de actuación puede:
          <ul style={{ margin: "6px 0 0 0", paddingLeft: 18 }}>
            <li>Actualizar el sub-tipo del caso</li>
            <li>Re-evaluar si se requieren insumos (desfavorables siempre los requieren)</li>
            <li>Reordenar la posición en cola</li>
          </ul>
          <div style={{ marginTop: 6 }}>El sistema lo aplicará automáticamente. Confirme para guardar.</div>
        </div>
        {err && (
          <div style={{ fontSize: 12, color: "var(--color-danger)" }}>{err}</div>
        )}
      </div>
    </CenteredModal>
  );
}
